<app-details-wrapper
  [title]="latestBudget()?.name"
  [subtitle]="
    (latestBudget()?.interval | i18n) + ' · ' + ('resets' | i18n) + ' ' + resetDateLabel()
  "
  [forceSmallScreen]="isSmallScreen()"
  [headerIconName]="'budget'"
  [headerIconWrapperClass]="
    'tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-solid tw-border-color-border-subtle tw-bg-color-surface-card'
  "
  [showDeleteButton]="true"
  (onBackClicked)="handleBackClick()"
  (onEditClicked)="editBudget()"
  (onDeleteClicked)="deleteBudget()"
>
  <div data-testid="budget-details" class="tw-flex tw-flex-col tw-gap-3 tw-rounded-lg tw-pb-3">
    <!-- Metric cards: Budget / Spent / Remaining -->
    <div class="tw-flex tw-flex-wrap tw-gap-3 sm:tw-gap-2">
      <ng-container
        *ngTemplateOutlet="
          metricCard;
          context: {
            key: 'budget',
            label: 'budget' | i18n,
            amount: budgetAmount(),
            toneClass: 'tw-text-dt-text-headings',
          }
        "
      ></ng-container>
      <ng-container
        *ngTemplateOutlet="
          metricCard;
          context: {
            key: 'spent',
            label: 'spent' | i18n,
            amount: currentAmountSpent(),
            toneClass: 'tw-text-dt-text-headings',
          }
        "
      ></ng-container>
      <ng-container
        *ngTemplateOutlet="
          metricCard;
          context: {
            key: 'remaining',
            label: 'remaining' | i18n,
            amount: remainingAmount(),
            toneClass: isOverBudget() ? 'tw-text-dt-text-error' : 'tw-text-dt-text-in-primary',
          }
        "
      ></ng-container>
    </div>

    <!-- Progress for the displayed interval -->
    <div
      class="tw-rounded-2xl tw-border tw-border-color-border-subtle tw-bg-color-surface-card tw-shadow-card-elevated tw-p-3"
    >
      <div class="tw-flex tw-justify-between tw-items-center tw-mb-2">
        <span
          class="tw-text-[13px] tw-font-semibold tw-uppercase tw-tracking-[0.04em] tw-text-dt-text-secondary"
          >{{ progressLabel() }}</span
        >
        <span class="tw-text-[13px] tw-font-medium tw-text-dt-text-secondary sm:tw-hidden"
          >{{ remainingDays() }} {{ "daysRemaining" | i18n }}</span
        >
      </div>

      <app-budget-progress-bar
        [emptyPercent]="progressBarEmptyWidth()"
        [colorClass]="statusBarClass(progressStatus())"
        size="lg"
      />

      <div class="tw-mt-2">
        <span
          class="tw-text-[14px] tw-font-medium tw-leading-[1.25] tw-tracking-[0.056px]"
          [ngClass]="{
            'tw-text-dt-text-error': progressStatus() === 'over',
            'tw-text-color-status-warning-text': progressStatus() === 'approaching',
            'tw-text-color-status-success-text': progressStatus() === 'under',
          }"
        >
          {{ usedPercentage() | number: "1.1-1" }}% {{ "used" | i18n }} —
          {{
            isBudgetReached()
              ? ("budgetReached" | i18n)
              : progressStatus() === "over"
                ? ("overBudget" | i18n)
                : progressStatus() === "approaching"
                  ? ("approaching" | i18n)
                  : ("underBudget" | i18n)
          }}
        </span>
      </div>

      <div class="tw-mb-2 tw-mt-3">
        <span
          class="tw-text-[13px] tw-font-semibold tw-uppercase tw-tracking-[0.04em] tw-text-dt-text-secondary"
          >{{ "daysRemaining" | i18n }}</span
        >
      </div>

      <app-budget-progress-bar
        [emptyPercent]="daysElapsedPercent()"
        [colorClass]="'tw-bg-color-icon-action'"
        size="lg"
      />

      <div class="tw-mt-2">
        <span
          class="tw-text-[14px] tw-font-medium tw-leading-[1.25] tw-tracking-[0.056px] tw-text-dt-text-secondary"
        >
          {{ remainingDays() }} {{ "daysRemaining" | i18n }}
        </span>
      </div>
    </div>

    <!-- Categories -->
    <div class="tw-flex tw-flex-col tw-gap-3">
      <span class="tw-text-[14px] tw-font-semibold tw-uppercase tw-text-dt-text-secondary">{{
        "categories" | i18n
      }}</span>

      <div
        class="tw-rounded-2xl tw-border tw-border-color-border-subtle tw-bg-color-surface-card tw-shadow-card-elevated tw-p-3"
      >
        <div
          appGlossScroll
          class="tw-flex tw-flex-col tw-max-h-[160px] tw-overflow-y-auto tw-overscroll-contain"
        >
          @for (
            budgetCategory of budgetCategories();
            track budgetCategory?.category?.name;
            let last = $last
          ) {
            <div
              class="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-py-3 tw-border-0 tw-border-b tw-border-b-color-border-subtle tw-shadow-drop-dark2"
              [ngClass]="{ 'tw-border-b-0': last }"
            >
              <span
                class="tw-min-w-0 tw-flex-1 tw-truncate tw-font-D-DIN-PRO tw-text-[14px] tw-leading-[125%] tw-tracking-[0.004em] tw-text-color-text-default"
                [title]="budgetCategory?.category?.name"
              >
                {{ budgetCategory?.category?.name }}
              </span>
              <div class="tw-flex tw-shrink-0 tw-items-center tw-gap-2">
                <span
                  class="tw-whitespace-nowrap tw-font-D-DIN-PRO tw-text-[14px] tw-leading-[125%] tw-tracking-[0.004em] tw-text-color-text-default"
                >
                  {{ absAmount(budgetCategory.amount) | number: "1.2-2" }}
                  <span
                    class="tw-font-sans tw-font-medium tw-text-[12px] tw-text-dt-text-tertiary"
                    >{{ detailCurrency() }}</span
                  >
                </span>
                @if (categoryBadge()) {
                  <span
                    class="tw-rounded-full tw-border tw-border-dt-border-error tw-bg-dt-surface-out tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-medium tw-uppercase tw-text-dt-text-error"
                  >
                    {{ "over" | i18n }}
                  </span>
                }
              </div>
            </div>
          }
        </div>
      </div>
    </div>

    <!-- Historical spending -->
    <div class="tw-flex tw-flex-col tw-gap-3">
      <span
        class="tw-text-[13px] tw-font-semibold tw-uppercase tw-tracking-[0.04em] tw-text-dt-text-secondary"
        >{{ "historicalSpendings" | i18n }}</span
      >
      <div
        class="tw-rounded-2xl tw-border tw-border-color-border-subtle tw-bg-color-surface-card tw-shadow-card-elevated tw-p-3"
      >
        @if (isCompactHistory()) {
          <div class="tw-flex tw-flex-col">
            @for (row of budgetHistoryTable?.data() ?? []; track row.month; let last = $last) {
              <div
                class="tw-flex tw-flex-col tw-gap-2 tw-py-3 tw-border-0 tw-border-b tw-border-b-color-border-subtle tw-shadow-drop-dark2"
                [ngClass]="{ 'tw-border-b-0': last }"
              >
                <div class="tw-flex tw-items-center tw-justify-between tw-gap-2">
                  <span
                    class="tw-min-w-0 tw-truncate tw-font-medium tw-text-[14px] tw-text-color-text-default"
                    [title]="row.month"
                    >{{ row.month }}</span
                  >
                  <span class="tw-flex tw-shrink-0 tw-items-center tw-gap-2">
                    <span
                      class="tw-font-D-DIN-PRO tw-text-[14px] tw-font-medium"
                      [ngClass]="varianceTextClass(row.variance ?? 0)"
                    >
                      {{ (row.variance ?? 0) > 0 ? "+" : ""
                      }}{{ row.variance ?? 0 | number: "1.2-2" }}
                    </span>
                    @if ((row.variance ?? 0) < 0) {
                      <span [ngClass]="varianceTagClass(row.variance ?? 0)">{{
                        "overspent" | i18n
                      }}</span>
                    }
                  </span>
                </div>
                <div
                  class="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-text-[12px] tw-text-color-text-secondary"
                >
                  <span class="tw-min-w-0 tw-truncate"
                    >{{ "budget" | i18n }}: {{ formatCurrency(row.budget) }}</span
                  >
                  <span class="tw-min-w-0 tw-truncate"
                    >{{ "actual" | i18n }}: {{ formatCurrency(absAmount(row.actual)) }}</span
                  >
                </div>
              </div>
            }
          </div>
        } @else {
          <div appGlossScroll class="tw-overflow-x-auto">
            <div class="tw-min-w-[240px]">
              <app-ag-grid-table
                [options]="budgetHistoryTable?.options"
                [data]="budgetHistoryTable?.data()"
                [hasBottomBorder]="true"
                [hasTopBorder]="true"
                [hasHeader]="true"
                [customGridClass]="'ag-form-bg'"
              />
            </div>
          </div>
        }
      </div>
    </div>

    <!-- Delete (small screen only; desktop uses the header trash icon). Driven by the same
         signal as details-wrapper's mobile header so UA-detected mobile devices wider than the
         sm breakpoint still get a delete affordance. -->
    @if (isSmallScreen()) {
      <div class="tw-w-full tw-p-4">
        <app-gloss-button
          [options]="{
            text: 'deleteBudget' | i18n,
            isEnabled: true,
            onClick: deleteBudget.bind(this),
          }"
          [fontClass]="'!tw-text-dt-text-error'"
        ></app-gloss-button>
      </div>
    }
  </div>
</app-details-wrapper>

<ng-template
  #metricCard
  let-key="key"
  let-label="label"
  let-amount="amount"
  let-toneClass="toneClass"
>
  <div
    [attr.data-testid]="'budget-details-' + key"
    class="tw-flex tw-min-w-[104px] tw-flex-1 tw-flex-col tw-gap-1 tw-rounded-2xl tw-border tw-border-color-border-subtle tw-bg-color-surface-card tw-shadow-card-elevated tw-p-3"
  >
    <span
      class="tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.04em] tw-text-dt-text-secondary"
    >
      {{ label }}
    </span>
    <span
      class="tw-font-D-DIN-PRO tw-font-medium tw-text-[18px] tw-leading-[125%] sm:tw-text-[15px]"
      [ngClass]="toneClass"
    >
      {{ amount | number: "1.2-2" }}
      <span class="tw-font-sans tw-font-[400] tw-text-[11px] tw-text-dt-text-headings">{{
        detailCurrency()
      }}</span>
    </span>
  </div>
</ng-template>
